/**
 * Skills taught or reviewed in Level 3, Lesson 1 (lesson1_nines.tex). Ids match the skills map (level-3.md).
 * Wording is the book's: "one row up or down on the Map", "the line decides", "back 1, then down a row",
 * "rename when you need to" / trade a ten, "write the digit, then a zero for every digit after it",
 * "the open mouth always eats the bigger number" (L3.12).
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { mathMap, ninePuzzle, roundingLine, underlined } from "../visuals";

const num = (n: number) => String(n);
const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);

/* ---------- ± 10: one row up or down on the Map ---------- */
export const plusMinus10: Skill = {
  id: "plus-minus-10",
  name: "Add or subtract 10",
  gen: (rng) => {
    const plus = rng() < 0.5;
    const n = plus ? int(rng, 11, 89) : int(rng, 20, 98);
    const ans = plus ? n + 10 : n - 10;
    const way = plus ? "down" : "up";
    return {
      id: `plus-minus-10:${n}${plus ? "+" : "-"}`,
      skill: "plus-minus-10",
      prompt: `${n} ${plus ? "+" : "−"} 10 =`,
      visual: mathMap({ mark: [n], caption: `Math Map with ${n} marked` }),
      input: { kind: "number" },
      answer: num(ans),
      diagnose: (raw) => {
        if (hit(raw, plus ? n + 1 : n - 1)) return { code: "pm10-one-step", hint: `That's one step along the row. Ten is a whole row: go ${way} one row.` };
        if (hit(raw, plus ? n - 10 : n + 10)) return { code: "pm10-wrong-way", hint: plus ? "Adding 10 goes DOWN a row, to the bigger numbers." : "Taking away 10 goes UP a row, to the smaller numbers." };
        return null;
      },
      hints: [
        `On the Map, ${plus ? "+ 10 is one row down" : "− 10 is one row up"}. Find ${n}.`,
        "Stay in the same column — the ones digit stays the same. Only the tens digit changes, by one.",
        `Start at ${n} and go one row ${way}, same column: ${ans}.`,
      ],
    };
  },
};

/* ---------- Number bonds: fingers up, fingers down ---------- */
export const bondFill: Skill = {
  id: "bond-fill",
  name: "Number bonds to 10",
  gen: (rng) => {
    const a = int(rng, 1, 9), b = 10 - a, front = rng() < 0.6;
    return {
      id: `bond-fill:${front ? `${a}+_` : `_+${a}`}`,
      skill: "bond-fill",
      prompt: front ? `${a} + __ = 10` : `__ + ${a} = 10`,
      input: { kind: "number" },
      answer: num(b),
      diagnose: (raw) => {
        if (a !== 5 && hit(raw, a)) return { code: "bond-said-up", hint: `${a} is how many are up. How many fingers are down?` };
        if (hit(raw, a + 10)) return { code: "bond-added", hint: "The two numbers together make 10. What goes with " + a + "?" };
        return null;
      },
      hints: [`Hold up ${a} fingers. How many are down?`, `Fingers up and fingers down always make ten.`, `${a} up, ${b} down: ${a} and ${b} make ten.`],
    };
  },
};

/* ---------- Rounding: the line decides ---------- */
function roundItem(n: number, to: 10 | 100): Item {
  const r10 = Math.round(n / 10) * 10, r100 = Math.round(n / 100) * 100;
  const ans = to === 10 ? r10 : r100;
  const s = String(n);
  const digit = Number(to === 10 ? s[s.length - 1] : s[s.length - 2]);
  const place = to === 10 ? "ones" : "tens";
  const dir = digit >= 5 ? "up" : "down";
  const other = digit >= 5 ? (to === 10 ? Math.floor(n / 10) * 10 : Math.floor(n / 100) * 100) : (to === 10 ? Math.floor(n / 10) * 10 + 10 : Math.floor(n / 100) * 100 + 100);
  return {
    id: `round-line:${n}/${to}`,
    skill: "round-line",
    prompt: `${n} to the nearest ${to}:`,
    visual: roundingLine(digit),
    input: { kind: "number" },
    answer: num(ans),
    diagnose: (raw) => {
      const other100 = to === 10 ? r100 : r10;
      if (other100 !== ans && hit(raw, other100)) return { code: "round-wrong-place", hint: `That's the nearest ${to === 10 ? 100 : 10}. For the nearest ${to}, look at the ${place} digit.` };
      if (hit(raw, other)) return { code: "round-wrong-way", hint: `Which side of the line is the ${digit}? 0 to 4 round down; 5 to 9 round up.` };
      if (raw.trim() && Number(raw) < n / 5) return { code: "round-dropped-zeros", hint: `Keep it the same size: ${n} rounds to a number close to ${n}.` };
      return null;
    },
    hints: [
      `Nearest ${to}: look at the ${place} digit. Which side of the line is it on?`,
      `The ${place} digit is ${digit}. ${digit} is on the ROUND ${dir.toUpperCase()} side.`,
      `${n}: the ${place} digit is ${digit}, ${digit >= 5 ? "right" : "left"} of the line, so round ${dir}: ${ans}.`,
    ],
  };
}
export const roundLine: Skill = {
  id: "round-line",
  name: "Round to the nearest 10 or 100",
  gen: (rng, level) => {
    let n = int(rng, 101, 989);
    // Early on, keep the deciding digit away from the line (not 4 or 5); later, the line itself and the carries (296 → 300).
    if (level < 2) while ([4, 5].includes(n % 10) || [4, 5].includes(Math.floor(n / 10) % 10)) n = int(rng, 101, 989);
    else if (rng() < 0.3) n = int(rng, 1, 9) * 100 + 90 + int(rng, 5, 9);
    return roundItem(n, rng() < 0.5 ? 10 : 100);
  },
};
export { roundItem };

/* ---------- Adding 9: back 1, down a row ---------- */
export function add9Item(n: number): Item {
  if (n === 0)
    return {
      id: "add-n-map-puzzle:9+0",
      skill: "add-n-map-puzzle",
      prompt: "The one puzzle that has no back: 9 + 0 =",
      input: { kind: "number" },
      answer: "9",
      hints: ["From zero you can't go back 1.", "If you can't go back, the answer is the end of the same line.", "Zero has no back, so the answer is the end of the same line: 9."],
    };
  const ans = n + 9;
  return {
    id: `add-n-map-puzzle:${n}+9`,
    skill: "add-n-map-puzzle",
    prompt: `${n} + 9 =`,
    visual: ninePuzzle(n),
    input: { kind: "number" },
    answer: num(ans),
    diagnose: (raw) => {
      if (hit(raw, n + 10)) return { code: "add9-no-back", hint: "You went down a row. Go back 1 first." };
      if (hit(raw, n + 11)) return { code: "add9-forward", hint: "Back 1, not forward 1. Then down a row." };
      if (hit(raw, n - 1)) return { code: "add9-no-down", hint: `That's back 1. Now go down a row.` };
      if (hit(raw, n + 8)) return { code: "add9-back2", hint: "Back just 1. Nine's bond is 1." };
      return null;
    },
    hints: [`Start at ${n}. Go back 1. Where are you?`, "Now go down one row, same column.", `${n}: back 1 is ${n - 1}, down a row is ${ans}.`],
  };
}
export const add9: Skill = {
  id: "add-n-map-puzzle",
  name: "Add 9: back 1 and down",
  gen: (rng, level) => {
    if (level >= 2 && rng() < 0.1) return add9Item(0);
    if (level === 0 && rng() < 0.5) return add9Item(int(rng, 1, 9));
    let n = int(rng, 11, 89);
    while (n % 10 === 0) n = int(rng, 11, 89);
    return add9Item(n);
  },
};

/* ---------- Review: 2-digit add and subtract, rename when you need to ---------- */
export const add2Vertical: Skill = {
  id: "add-2digit-vertical",
  name: "Add — rename when you need to",
  gen: (rng) => {
    let a = 0, b = 0;
    do { a = int(rng, 15, 69); b = int(rng, 12, 49); } while ((a % 10) + (b % 10) < 10 || a + b >= 100);
    const ans = a + b, o = (a % 10) + (b % 10), t = Math.floor(a / 10) + Math.floor(b / 10);
    return {
      id: `add-2digit-vertical:${a}+${b}`,
      skill: "add-2digit-vertical",
      prompt: "Add. Rename when you need to.",
      input: { kind: "vertical", top: a, bottom: b, op: "+" },
      speak: `Add ${a} plus ${b}. Rename when you need to.`,
      answer: num(ans),
      diagnose: (raw) => {
        if (hit(raw, ans - 10)) return { code: "add-lost-ten", hint: "The ones made a new ten. Did it go into the tens column?" };
        if (hit(raw, Number(`${t}${o}`))) return { code: "add-two-digits-one-place", hint: "A place only holds one digit. Ten ones trade for a ten — where does that ten go?" };
        return null;
      },
      hints: [`Start with the ones: ${a % 10} + ${b % 10}.`, `${o} ones is a ten and ${o - 10} ones. Trade the ten into the tens column.`, `${a % 10} + ${b % 10} = ${o}: write ${o - 10}, trade the ten to the tens. ${Math.floor(a / 10)} + ${Math.floor(b / 10)} + 1 = ${t + 1} tens. ${ans}.`],
    };
  },
};

export const sub2Vertical: Skill = {
  id: "sub-2digit-vertical",
  name: "Subtract — rename when you need to",
  gen: (rng) => {
    let a = 0, b = 0;
    do { a = int(rng, 41, 98); b = int(rng, 12, 59); } while ((a % 10) >= (b % 10) || b >= a);
    const ans = a - b, ao = a % 10, bo = b % 10, at = Math.floor(a / 10), bt = Math.floor(b / 10);
    const flipped = Number(`${at - bt}${bo - ao}`);
    return {
      id: `sub-2digit-vertical:${a}-${b}`,
      skill: "sub-2digit-vertical",
      prompt: "Subtract. Rename when you need to.",
      input: { kind: "vertical", top: a, bottom: b, op: "−" },
      speak: `Subtract ${a} minus ${b}. Rename when you need to.`,
      answer: num(ans),
      diagnose: (raw) => {
        if (hit(raw, flipped)) return { code: "sub-smaller-from-larger", hint: `In the ones, ${ao} − ${bo}: there aren't enough ones. Go get a ten.` };
        if (hit(raw, ans + 10)) return { code: "sub-kept-ten", hint: "You took a ten to make more ones. Did you cross it off the tens?" };
        return null;
      },
      hints: [`Start with the ones: ${ao} − ${bo} — are there enough ones?`, `Not enough — go get a ten. ${a} becomes ${at - 1} tens and ${ao + 10} ones.`, `${ao + 10} − ${bo} = ${ao + 10 - bo}. ${at - 1} − ${bt} = ${at - 1 - bt} tens. ${ans}.`],
    };
  },
};

/* ---------- Review: place value, the underlined digit ---------- */
export const placeValueUnderlined: Skill = {
  id: "place-value-underlined",
  name: "Value of the underlined digit",
  gen: (rng) => {
    let n = int(rng, 102, 987), i = int(rng, 0, 1);
    while (String(n)[i] === "0") n = int(rng, 102, 987);
    const d = Number(String(n)[i]), val = d * 10 ** (2 - i), place = i === 0 ? "hundreds" : "tens";
    return {
      id: `place-value-underlined:${n}/${i}`,
      skill: "place-value-underlined",
      prompt: `The value of the underlined digit:`,
      visual: underlined(n, i),
      input: { kind: "number" },
      answer: num(val),
      diagnose: (raw) => {
        if (hit(raw, d)) return { code: "pv-digit-only", hint: `The ${d} is in the ${place} place. What is it worth?` };
        if (raw.trim().startsWith(String(d)) && !hit(raw, val)) return { code: "pv-zeros", hint: "Write the digit, then a zero for every digit after it." };
        return null;
      },
      hints: [`Which place is the underlined ${d} in?`, "Write the digit, then a zero for every digit after it.", `The ${d} is in the ${place} place, with ${2 - i} digit${i === 0 ? "s" : ""} after it: ${val}.`],
    };
  },
};

/* ---------- Review: compare ---------- */
export const compareWhole: Skill = {
  id: "compare-whole",
  name: "Compare with <, >, =",
  gen: (rng) => {
    const kind = pick(rng, ["swap", "swap", "short", "equal"] as const);
    let a = int(rng, 102, 987), b = a;
    if (kind === "swap") { const s = String(a); b = Number(s[0]! + s[2] + s[1]); if (b === a) b = a + 10; }
    else if (kind === "short") { const s = String(a); b = Number(s[0]! + s[2]); } // 706 vs 76, as in the book
    if (rng() < 0.5) [a, b] = [b, a];
    const ans = a > b ? ">" : a < b ? "<" : "=";
    const wrong = ans === ">" ? "<" : ans === "<" ? ">" : "";
    return {
      id: `compare-whole:${a}?${b}`,
      skill: "compare-whole",
      prompt: `${a} ___ ${b}`,
      input: { kind: "choice", options: ["<", ">", "="] },
      answer: ans,
      diagnose: (raw) => (wrong && raw.trim() === wrong ? { code: "compare-flipped", hint: "You found the bigger one — now the symbol: the open mouth always eats the bigger number." } : null),
      hints: ["Stack them: compare the biggest place first.", "The open mouth always eats the bigger number.", `${Math.max(a, b)} is ${a === b ? "the same as" : "bigger than"} ${Math.min(a, b)}${a === b ? "" : ", so the open mouth faces " + Math.max(a, b)}: ${a} ${ans} ${b}.`],
    };
  },
};

export const LEVEL3_L1_SKILLS: Skill[] = [plusMinus10, bondFill, roundLine, add9, add2Vertical, sub2Vertical, placeValueUnderlined, compareWhole];
